import type { FC, ReactElement } from 'react';

import { UserIcon } from '@phosphor-icons/react';
import { getCookie } from 'cookies-next';
import { memo, useEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { useUserContext } from '../../../../utilities/context/dynamic/UserContext';
import { useAppContext } from '../../../../utilities/context/static/AppContext';
import LoginIcon from '../../core/icons/LoginIcon';
import { FesLink } from '../../core/link/FesLink';
import { media } from '../../core/styles';
import { HeaderButton } from '../HeaderStyle';
import { CodeFeatureFlag } from '~/config/code-feature-flags';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

type LoginButtonProps = {
  className?: string;
  loginLabel?: string;
  onClick?: () => void;
};

type LoginIconComponentProps = {
  size: string;
  color: string;
};

const S = {
  LoginButton: styled(FesLink)`
    width: 100%;
    ${({ theme }) => css`
      min-width: ${theme.components.icon.topNavigation.size};
      line-height: var(--top-nav-height);
      ${HeaderButton}

      &:hover,
      &:focus {
        path {
          fill: ${theme.colors.content.component.header.hover};
        }

        span {
          border-color: ${theme.colors.content.component.header.color};
          color: ${theme.colors.content.component.header.color};
        }

        ${getSiteStyles(theme.siteType, {
          [SiteType.Outlet]: css`
            path {
              fill: ${theme.colors.background.component.button.primary.hover};
            }

            span {
              color: ${theme.colors.background.component.button.primary.hover};
            }
          `,
        })}
      }
    `}
  `,

  IconWrapper: styled.span`
    ${({ theme }) => css`
      display: inline-block;
      width: ${theme.components.icon.topNavigation.size};
      height: 100%;
    `}
  `,

  Title: styled.span`
    ${({ theme }) => css`
      color: ${theme.colors.content.component.header.color};
      max-width: 115px;
      line-height: normal;
      font-size: ${theme.font.size[3]};
      font-weight: ${theme.variables.font.weight.medium};
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      padding-left: ${theme.padding['2xs']};

      @media ${media(theme).greaterThan('sm')} {
        display: none;
      }
    `}
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const CustomLoginIcon = ({ size, color }: LoginIconComponentProps): ReactElement => (
  <S.IconWrapper>
    <LoginIcon color={color} />
  </S.IconWrapper>
);

const PhosphorUserIcon = ({ size, color }: LoginIconComponentProps): ReactElement => (
  <UserIcon size={size} color={color} weight="regular" />
);

const LoginIconMap: Record<SiteType, FC<LoginIconComponentProps>> = {
  [SiteType.Aaf]: PhosphorUserIcon,
  [SiteType.Employeeshop]: CustomLoginIcon,
  [SiteType.Gstar]: CustomLoginIcon,
  [SiteType.Outlet]: CustomLoginIcon,
  [SiteType.Wahts]: PhosphorUserIcon,
};

const LoginButtonComponent = ({ className, loginLabel, onClick }: LoginButtonProps) => {
  const [firstNameFromCookie, setFirstNameFromCookie] = useState<string>();
  const { locale, siteType } = useAppContext();
  const { user, loggedInRender } = useUserContext();
  const theme = useTheme();

  const LoginIcon = LoginIconMap[siteType];
  const { showLoginButtonLabel } = CodeFeatureFlag[siteType];

  useEffect(() => {
    const firstName = getCookie('firstName')?.toString();

    if (firstName) {
      setFirstNameFromCookie(firstName);
    }
  }, []);

  const displayLabel = (() => {
    if (!user) {
      return firstNameFromCookie;
    }

    if (loggedInRender) {
      return user?.firstName || user?.name;
    }

    return loginLabel;
  })();

  return (
    <S.LoginButton
      aria-label={loginLabel}
      href={loggedInRender ? `/${locale}/account` : `/${locale}/account/create`}
      data-testid="login-button"
      data-cs-capture=""
      className={className}
      onClick={onClick}
    >
      <LoginIcon
        size={theme.components.icon.topNavigation.size}
        color={theme.colors.content.primary}
      />
      {showLoginButtonLabel && <S.Title>{displayLabel}</S.Title>}
    </S.LoginButton>
  );
};

export const LoginButton = memo(LoginButtonComponent);
